import { format } from "date-fns";
import intl from "react-intl-universal";
import {
  downloadCsv,
  endOfDay,
  formatFilterAmount,
  formatFilterDate,
  labelById,
  parseAmountInput,
  startOfDay,
  toggleFilterListValue,
} from "../../../Components/Molecules/GridHistory/gridFilterUtils";
import { getMovementKindMeta } from "./constants";
import {
  getCiPaymentMethodOptions,
  resolveCiPaymentMethodId,
} from "./contaInternacionalHistoryFilters";

export { toggleFilterListValue };

export const EMPTY_MOVEMENT_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  kinds: [],
  statuses: [],
  paymentMethods: [],
  searchQuery: "",
};

export const getMovementKindOptions = () =>
  Object.entries(getMovementKindMeta()).map(([id, meta]) => ({
    id,
    label: meta.label,
  }));

/** @deprecated Use getMovementKindOptions() at render time for i18n */
export const MOVEMENT_KIND_OPTIONS = Object.entries(getMovementKindMeta()).map(([id, meta]) => ({
  id,
  label: meta.label,
}));

export const getMovementStatusOptions = () => [
  { id: "concluida", label: intl.get("contaInternacional.statusCompleted") },
  { id: "processando", label: intl.get("contaInternacional.statusProcessing") },
];

/** @deprecated Use getMovementStatusOptions() at render time for i18n */
export const MOVEMENT_STATUS_OPTIONS = [
  { id: "concluida", label: "Concluída" },
  { id: "processando", label: "Em processamento" },
];

const parseMovementDate = (dateStr) => {
  const [datePart, timePart = "00:00"] = dateStr.split(" ");
  const [day, month, year] = datePart.split("/").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};

const normalizeStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("process")) return "processando";
  return "concluida";
};

export const getMovementPaymentMethodId = (item) => {
  if (item?.paymentMethod) {
    return resolveCiPaymentMethodId(item.paymentMethod);
  }

  return resolveCiPaymentMethodId(item?.description);
};

export const hasActiveMovementFilters = (filters) =>
  Boolean(filters.dateFrom)
  || Boolean(filters.dateTo)
  || filters.amountMin !== ""
  || filters.amountMax !== ""
  || filters.kinds.length > 0
  || filters.statuses.length > 0
  || filters.paymentMethods.length > 0
  || Boolean(filters.searchQuery?.trim());

export const applyMovementFilters = (items, filters) => {
  const minAmount = parseAmountInput(filters.amountMin);
  const maxAmount = parseAmountInput(filters.amountMax);
  const dateFrom = filters.dateFrom ? startOfDay(filters.dateFrom) : null;
  const dateTo = filters.dateTo ? endOfDay(filters.dateTo) : null;

  return items.filter((item) => {
    const itemDate = parseMovementDate(item.date);

    if (dateFrom && itemDate < dateFrom) return false;
    if (dateTo && itemDate > dateTo) return false;

    const amount = Math.abs(Number(item.amount) || 0);
    if (minAmount !== null && amount < minAmount) return false;
    if (maxAmount !== null && amount > maxAmount) return false;

    if (filters.kinds.length > 0 && !filters.kinds.includes(item.kind)) return false;

    if (filters.statuses.length > 0) {
      const itemStatus = normalizeStatus(item.status);
      if (!filters.statuses.includes(itemStatus)) return false;
    }

    if (filters.paymentMethods.length > 0) {
      const paymentMethodId = getMovementPaymentMethodId(item);
      if (!paymentMethodId || !filters.paymentMethods.includes(paymentMethodId)) return false;
    }

    if (filters.searchQuery?.trim()) {
      const query = filters.searchQuery.trim().toLowerCase();
      const haystack = [
        item.id,
        item.date,
        item.status,
        item.kind,
        item.description,
        item.currency,
        item.counterCurrency,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }

    return true;
  });
};

export const buildMovementFilterTags = (filters) => {
  const tags = [];

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom) {
    tags.push({
      key: "dateFrom",
      iconType: "date",
      value: formatFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    tags.push({
      key: "dateTo",
      iconType: "date",
      value: formatFilterDate(filters.dateTo),
    });
  }

  if (filters.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMin),
    });
  }

  if (filters.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMax),
    });
  }

  if (filters.kinds.length > 0) {
    tags.push({
      key: "kinds",
      iconType: "type",
      value: filters.kinds.map((kind) => labelById(getMovementKindOptions(), kind)).join(", "),
    });
  }

  if (filters.statuses.length > 0) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((status) => labelById(getMovementStatusOptions(), status))
        .join(", "),
    });
  }

  if (filters.paymentMethods.length > 0) {
    tags.push({
      key: "paymentMethods",
      iconType: "payment",
      value: filters.paymentMethods
        .map((method) => labelById(getCiPaymentMethodOptions(), method))
        .join(", "),
    });
  }

  return tags;
};

export const removeMovementFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "dateFrom":
      return { ...filters, dateFrom: null };
    case "dateTo":
      return { ...filters, dateTo: null };
    case "amountMin":
      return { ...filters, amountMin: "" };
    case "amountMax":
      return { ...filters, amountMax: "" };
    case "kinds":
      return { ...filters, kinds: [] };
    case "statuses":
      return { ...filters, statuses: [] };
    case "paymentMethods":
      return { ...filters, paymentMethods: [] };
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    default:
      return filters;
  }
};

export const isMovementChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return filters.amountMin !== "" || filters.amountMax !== "";
    case "type":
      return filters.kinds.length > 0;
    case "status":
      return filters.statuses.length > 0;
    case "payment":
      return filters.paymentMethods.length > 0;
    case "more":
      return filters.statuses.length > 0 || filters.paymentMethods.length > 0;
    default:
      return false;
  }
};

export const exportMovementsCsv = (items, currencyId) => {
  const headers = ["Data", "Tipo", "Descrição", "Valor", "Status", "Moeda"];

  const rows = items.map((item) => [
    item.date,
    labelById(getMovementKindOptions(), item.kind),
    item.description || "",
    String(Math.abs(item.amount)).replace(".", ","),
    item.status,
    item.currency?.toUpperCase() || currencyId?.toUpperCase() || "",
  ]);

  downloadCsv(
    headers,
    rows,
    `movimentacoes-${currencyId || "conta"}-${format(new Date(), "yyyy-MM-dd")}.csv`,
  );
};
